import React, { useLayoutEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import useUserStore from 'store/useUserStore';
import { openErrorToaster } from 'services/toast.service';
import { canAccessRule, getDeniedRedirect, getRuleById } from './routeAccess';
import { usePermissions } from './usePermission';

const ROUTE_DENIED_TOAST_MS = 10000;

export type RouteAccessGuardProps = {
  ruleId: string;
  children: React.ReactNode;
  redirectTo?: string;
};

export default function RouteAccessGuard({
  ruleId,
  children,
  redirectTo,
}: RouteAccessGuardProps): React.ReactNode {
  const user = useUserStore(s => s.user);
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const rule = getRuleById(ruleId);
  const deniedRedirect = redirectTo ?? getDeniedRedirect(rule);
  const mayAccess = canAccessRule(rule, can);
  const denyToastGuardRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!user || mayAccess) {
      denyToastGuardRef.current = null;
      return;
    }
    const dedupeKey = `${ruleId}:${location.pathname}`;
    if (denyToastGuardRef.current === dedupeKey) {
      return;
    }
    denyToastGuardRef.current = dedupeKey;

    const toastStorageKey = `my_txt_route_403_toast:${dedupeKey}`;
    let showToast = true;
    try {
      const prev = sessionStorage.getItem(toastStorageKey);
      if (prev && Date.now() - Number(prev) < 2000) {
        showToast = false;
      } else {
        sessionStorage.setItem(toastStorageKey, String(Date.now()));
      }
    } catch {
      // ignore quota / private mode
    }

    if (showToast) {
      openErrorToaster(
        'You do not have permission to access this page. (403)',
        ROUTE_DENIED_TOAST_MS,
        'Access denied',
      );
    }
    navigate(deniedRedirect, { replace: true });
  }, [
    user,
    mayAccess,
    deniedRedirect,
    navigate,
    ruleId,
    location.pathname,
  ]);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!mayAccess) {
    return null;
  }

  return children;
}
